import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import MLBPlayerCardModal from '@/components/mlb-beta/MLBPlayerCardModal';
import { getWeightEntries } from '@/lib/trashScoring';
import { formatBareDate } from '@/lib/etDate';

export interface UpcomingGame {
  date: string;       // YYYY-MM-DD
  opponent: string;   // e.g. "CHC"
  isHome: boolean;
}

export interface BoxScorePlayer {
  player_id: string;
  player_name: string;
  position: string;
  team_name: string;
  headshot_url?: string | null;
  is_starter: boolean;
  slot?: string;
  stats: Record<string, number>;
  total_points: number;
  trash_points?: number;
  anti_points?: number;
  upcoming_games?: UpcomingGame[];
}

interface MatchupBoxScoreProps {
  players: BoxScorePlayer[];
  teamName: string;
  teamScore: number;
  isWinner?: boolean;
  compact?: boolean;
  scoringWeights?: Record<string, number>;
  // New: side-by-side mode
  awayPlayers?: BoxScorePlayer[];
  homePlayers?: BoxScorePlayer[];
  awayTeamName?: string;
  homeTeamName?: string;
  awayScore?: number;
  homeScore?: number;
  probablePitcherIds?: Set<string>;
  postponedPlayerIds?: Set<string>;
}

// Canonical slot order — normalize slot names (strip trailing digits) and rank them
const SLOT_RANK: Record<string, number> = { C: 0, '1B': 1, '2B': 2, SS: 3, '3B': 4, OF: 5, DH: 6, UTIL: 7, SP: 8, RP: 9, P: 10 };

function normalizeSlot(slot: string): string {
  return slot.replace(/\d+$/, '').toUpperCase();
}

function slotSortKey(slot: string): number {
  const norm = normalizeSlot(slot);
  return SLOT_RANK[norm] ?? 99;
}

function displaySlotLabel(slot: string): string {
  const norm = normalizeSlot(slot);
  // Normalize SP/RP to P for combined pitcher leagues
  if (norm === 'SP' || norm === 'RP') return 'P';
  if (norm === 'DROPPED') return '-';
  return norm;
}

function formatIP(ip: number): string {
  const full = Math.floor(ip);
  const thirds = Math.round((ip - full) * 3);
  return `${full}.${thirds}`;
}

function getKeyStats(stats: Record<string, number>, isPitcher: boolean) {
  if (isPitcher && (stats.innings_pitched || 0) > 0) {
    return {
      labels: ['IP', 'H', 'ER', 'BB', 'K'],
      values: [
        formatIP(stats.innings_pitched || 0),
        String(stats.hits_allowed || 0),
        String(stats.earned_runs || 0),
        String(stats.walks_allowed || 0),
        String(stats.pitcher_strikeouts || 0),
      ],
    };
  }
  if ((stats.at_bats || 0) > 0) {
    return {
      labels: ['AB', 'H', 'K', 'HR', 'RBI'],
      values: [
        String(stats.at_bats || 0),
        String(stats.hits || 0),
        String(stats.strikeouts || 0),
        String(stats.home_runs || 0),
        String(stats.rbi || 0),
      ],
    };
  }
  return null;
}

const MatchupBoxScore = (props: MatchupBoxScoreProps) => {
  const { scoringWeights } = props;
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  const [modalPlayerId, setModalPlayerId] = useState<string | null>(null);

  // If side-by-side data is provided, use the new layout
  if (props.awayPlayers && props.homePlayers) {
    return (
      <SideBySideBoxScore
        awayPlayers={props.awayPlayers}
        homePlayers={props.homePlayers}
        awayTeamName={props.awayTeamName || ''}
        homeTeamName={props.homeTeamName || ''}
        awayScore={props.awayScore ?? 0}
        homeScore={props.homeScore ?? 0}
        scoringWeights={scoringWeights}
        probablePitcherIds={props.probablePitcherIds}
        postponedPlayerIds={props.postponedPlayerIds}
      />
    );
  }

  // Legacy single-team layout (kept for backward compatibility)
  const { players, teamName, teamScore, isWinner, compact } = props;
  const starters = players.filter(p => p.is_starter).sort((a, b) => b.total_points - a.total_points);
  const benchPlayers = players.filter(p => !p.is_starter);

  const renderPlayerBreakdown = (player: BoxScorePlayer) => {
    if (!scoringWeights) return null;
    return <PlayerBreakdown player={player} scoringWeights={scoringWeights} />;
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/40 rounded-t-lg">
        <span className={cn("text-sm font-bold", isWinner ? "text-primary" : "text-foreground")}>{teamName}</span>
        <span className={cn("text-lg font-bold font-heading", isWinner ? "text-primary" : "text-foreground")}>{teamScore.toFixed(1)}</span>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="px-3 py-1 bg-muted/20 border-b border-border/50">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Starters ({starters.length})</span>
        </div>
        {starters.map(player => {
          const isExpanded = expandedPlayer === player.player_id;
          const hasStats = Object.values(player.stats).some(v => v > 0);
          return (
            <div key={player.player_id}>
              <div
                className={cn("flex items-center gap-2 px-3 py-2 transition-colors", hasStats && "cursor-pointer hover:bg-muted/30", isExpanded && "bg-muted/20")}
                onClick={() => hasStats && setExpandedPlayer(isExpanded ? null : player.player_id)}
              >
                <div className="flex-1 min-w-0">
                  <button className="text-xs font-medium text-foreground hover:text-primary transition-colors truncate block" onClick={(e) => { e.stopPropagation(); setModalPlayerId(player.player_id); }}>
                    {player.player_name}
                  </button>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground">{player.position} • {player.team_name}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={cn("text-sm font-bold", player.total_points > 0 ? "text-accent" : player.total_points < 0 ? "text-destructive" : "text-muted-foreground")}>
                    {player.total_points > 0 ? '+' : ''}{player.total_points.toFixed(1)}
                  </span>
                </div>
              </div>
              {isExpanded && renderPlayerBreakdown(player)}
            </div>
          );
        })}
      </div>
      {benchPlayers.length > 0 && !compact && (
        <div className="border border-border/50 rounded-lg overflow-hidden opacity-60">
          <div className="px-3 py-1 bg-muted/10 border-b border-border/50">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Bench ({benchPlayers.length})</span>
          </div>
          {benchPlayers.map(p => (
            <div key={p.player_id} className="flex items-center gap-2 px-3 py-1.5">
              <span className="text-xs text-muted-foreground truncate">{p.player_name}</span>
              <span className="text-[10px] text-muted-foreground ml-auto">{p.position}</span>
            </div>
          ))}
        </div>
      )}
      {modalPlayerId && (
        <MLBPlayerCardModal playerId={modalPlayerId} open={!!modalPlayerId} onOpenChange={(open) => { if (!open) setModalPlayerId(null); }} />
      )}
    </div>
  );
};

// ─── SCORING BREAKDOWN ──────────────────────────────────────────────
function PlayerBreakdown({ player, scoringWeights }: { player: BoxScorePlayer; scoringWeights: Record<string, number> }) {
  const weightEntries = getWeightEntries(scoringWeights);

  const contributions = weightEntries.map(({ key, label, weight }) => {
    let raw = 0;
    if (key === '__derived_outs') raw = (player.stats.at_bats || 0) - (player.stats.hits || 0);
    else if (key === '__derived_total_bases') raw = (player.stats.hits || 0) + (player.stats.doubles || 0) + 2 * (player.stats.triples || 0) + 3 * (player.stats.home_runs || 0);
    else if (key === '__derived_pitcher_outs') raw = Math.round((player.stats.innings_pitched || 0) * 3);
    else raw = player.stats[key] || 0;
    return { key, label, weight, raw, pts: raw * weight };
  }).filter(c => c.raw > 0);

  const trashContributions = contributions.filter(c => c.weight > 0);
  const antiContributions = contributions.filter(c => c.weight < 0);
  // Use pre-computed engine values when available, fall back to contribution sums
  const trashTotal = player.trash_points ?? trashContributions.reduce((s, c) => s + c.pts, 0);
  const antiTotal = player.anti_points ?? (player.total_points - trashTotal);

  return (
    <div className="px-3 py-2 bg-muted/20 border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
      {trashContributions.length > 0 && (
        <div className="mb-2">
          <span className="text-[10px] font-semibold text-accent uppercase tracking-wider">Trash Stats (+{trashTotal.toFixed(1)})</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {trashContributions.map(c => (
              <Badge key={c.key} variant="outline" className="text-[10px] px-1.5 h-5 gap-1 border-accent/30 text-accent">
                {c.raw}× {c.label} <span className="text-accent/70">= +{c.pts.toFixed(1)}</span>
              </Badge>
            ))}
          </div>
        </div>
      )}
      {antiContributions.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold text-destructive uppercase tracking-wider">Anti-Stats ({antiTotal.toFixed(1)})</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {antiContributions.map(c => (
              <Badge key={c.key} variant="outline" className="text-[10px] px-1.5 h-5 gap-1 border-destructive/30 text-destructive">
                {c.raw}× {c.label} <span className="text-destructive/70">= {c.pts.toFixed(1)}</span>
              </Badge>
            ))}
          </div>
        </div>
      )}
      {trashContributions.length === 0 && antiContributions.length === 0 && (
        <p className="text-[10px] text-muted-foreground italic">No stats recorded this week</p>
      )}
    </div>
  );
}

// ─── SIDE-BY-SIDE FANTRAX LAYOUT ────────────────────────────────────
function SideBySideBoxScore({
  awayPlayers,
  homePlayers,
  awayTeamName,
  homeTeamName,
  awayScore,
  homeScore,
  scoringWeights,
  probablePitcherIds,
  postponedPlayerIds,
}: {
  awayPlayers: BoxScorePlayer[];
  homePlayers: BoxScorePlayer[];
  awayTeamName: string;
  homeTeamName: string;
  awayScore: number;
  homeScore: number;
  scoringWeights?: Record<string, number>;
  probablePitcherIds?: Set<string>;
  postponedPlayerIds?: Set<string>;
}) {
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  const [modalPlayerId, setModalPlayerId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [startersOpen, setStartersOpen] = useState(true);
  const [benchOpen, setBenchOpen] = useState(true);
  const [droppedOpen, setDroppedOpen] = useState(false);

  // Detect mobile on mount and on resize
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    check();
    // On mobile, default both sections to collapsed
    if (window.innerWidth < 768) {
      setStartersOpen(false);
      setBenchOpen(false);
    }
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Sort each team's starters independently by slot order
  const sortBySlot = (a: BoxScorePlayer, b: BoxScorePlayer) => slotSortKey(a.slot || 'UTIL') - slotSortKey(b.slot || 'UTIL');
  const awayStarters = awayPlayers.filter(p => p.is_starter).sort(sortBySlot);
  const homeStarters = homePlayers.filter(p => p.is_starter).sort(sortBySlot);
  const awayBench = awayPlayers.filter(p => !p.is_starter && p.slot !== 'DROPPED');
  const homeBench = homePlayers.filter(p => !p.is_starter && p.slot !== 'DROPPED');
  const awayDropped = awayPlayers.filter(p => p.slot === 'DROPPED');
  const homeDropped = homePlayers.filter(p => p.slot === 'DROPPED');

  // Point subtotals for breakdown
  const sumPts = (ps: BoxScorePlayer[]) => Math.round(ps.reduce((s, p) => s + p.total_points, 0) * 10) / 10;
  const awayStarterPts = sumPts(awayStarters);
  const homeStarterPts = sumPts(homeStarters);
  const awayBenchPts = sumPts(awayBench);
  const homeBenchPts = sumPts(homeBench);
  const awayDroppedPts = sumPts(awayDropped);
  const homeDroppedPts = sumPts(homeDropped);
  const showBreakdown = awayBenchPts !== 0 || homeBenchPts !== 0 || awayDroppedPts !== 0 || homeDroppedPts !== 0;

  // Build rows: each team gets its own column, no cross-team slot matching
  const numStarterRows = Math.max(awayStarters.length, homeStarters.length);

  const awayWinning = awayScore > homeScore;
  const homeWinning = homeScore > awayScore;

  const renderPlayerCell = (player: BoxScorePlayer | undefined, side: 'away' | 'home') => {
    if (!player) {
      return (
        <div className="flex items-center justify-center py-2 px-2">
          <span className="text-[10px] text-muted-foreground/40 italic">Empty</span>
        </div>
      );
    }

    const isExpanded = expandedPlayer === player.player_id;
    const hasStats = Object.values(player.stats).some(v => v > 0);
    const isPitcher = !!(player.stats.innings_pitched && player.stats.innings_pitched > 0);
    const keyStats = getKeyStats(player.stats, isPitcher);
    const align = side === 'away' ? 'text-right' : 'text-left';
    const flexDir = side === 'away' ? 'flex-row-reverse' : 'flex-row';
    const isDropped = player.slot === 'DROPPED';

    return (
      <div className={cn(isDropped && "opacity-60")}>
        <div
          className={cn(
            "px-2 py-1.5 transition-colors",
            hasStats && "cursor-pointer hover:bg-muted/30",
            isExpanded && "bg-muted/20",
          )}
          onClick={() => hasStats && setExpandedPlayer(isExpanded ? null : player.player_id)}
        >
          {/* Player name + TP */}
          <div className={cn("flex items-center gap-1.5", flexDir)}>
            <span className={cn(
              "text-sm font-bold tabular-nums shrink-0",
              player.total_points > 0 ? "text-accent" : player.total_points < 0 ? "text-destructive" : "text-muted-foreground",
            )}>
              {player.total_points > 0 ? '+' : ''}{player.total_points.toFixed(1)}
            </span>
            <button
              className={cn("text-[11px] font-medium text-foreground hover:text-primary transition-colors truncate", align)}
              onClick={(e) => { e.stopPropagation(); setModalPlayerId(player.player_id); }}
            >
              {player.player_name}
            </button>
            {probablePitcherIds?.has(player.player_id) && (
              <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-orange-400/60 text-orange-500 bg-orange-500/10 font-semibold shrink-0">PP</Badge>
            )}
            {postponedPlayerIds?.has(player.player_id) && (
              <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-yellow-500/60 text-yellow-500 bg-yellow-500/10 font-semibold shrink-0">PPD</Badge>
            )}
            {isDropped && <span className="text-[8px] font-semibold uppercase text-destructive/70 shrink-0">Dropped</span>}
          </div>

          {/* Team + key stats or upcoming games */}
          <div className={cn("flex items-center gap-1 mt-0.5 flex-wrap", flexDir)}>
            <span className="text-[9px] text-muted-foreground shrink-0">{player.team_name}</span>
            {keyStats && (
              <div className={cn("flex items-center gap-0.5", flexDir)}>
                {keyStats.labels.map((label, i) => (
                  <span key={label} className="text-[9px] text-muted-foreground">
                    {i > 0 && <span className="mx-0.5">·</span>}
                    <span className="text-muted-foreground/60">{label}</span>{' '}
                    <span className="text-foreground/70">{keyStats.values[i]}</span>
                  </span>
                ))}
              </div>
            )}
            {!keyStats && !hasStats && player.upcoming_games && player.upcoming_games.length > 0 && (
              <div className={cn("flex items-center gap-0.5 flex-wrap", flexDir)}>
                {player.upcoming_games.slice(0, 3).map((g, i) => (
                  <span key={g.date} className="text-[9px] text-muted-foreground">
                    {i > 0 && <span className="mx-0.5">·</span>}
                    <span className="text-muted-foreground/60">
                      {formatBareDate(g.date, { month: 'short', day: 'numeric' })}
                    </span>{' '}
                    <span className="text-foreground/70">{g.isHome ? 'vs' : '@'} {g.opponent}</span>
                  </span>
                ))}
                {player.upcoming_games.length > 3 && (
                  <span className="text-[9px] text-muted-foreground/50">+{player.upcoming_games.length - 3} more</span>
                )}
              </div>
            )}
            {!keyStats && hasStats && (
              <span className="text-[9px] text-muted-foreground/50 italic">no AB/IP</span>
            )}
          </div>
        </div>
        {isExpanded && scoringWeights && (
          <PlayerBreakdown player={player} scoringWeights={scoringWeights} />
        )}
      </div>
    );
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Team headers */}
      <div className="grid grid-cols-[1fr_40px_1fr] bg-muted/40">
        <div className="px-3 py-2 text-right">
          <span className={cn("text-xs font-bold", awayWinning ? "text-primary" : "text-foreground")}>{awayTeamName}</span>
          <span className={cn("text-lg font-bold font-heading ml-2", awayWinning ? "text-primary" : "text-foreground")}>{awayScore.toFixed(1)}</span>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-[10px] font-bold text-muted-foreground">VS</span>
        </div>
        <div className="px-3 py-2">
          <span className={cn("text-lg font-bold font-heading mr-2", homeWinning ? "text-primary" : "text-foreground")}>{homeScore.toFixed(1)}</span>
          <span className={cn("text-xs font-bold", homeWinning ? "text-primary" : "text-foreground")}>{homeTeamName}</span>
        </div>
      </div>

      {/* Starters section header — collapsible on mobile */}
      <div
        className={cn(
          "grid grid-cols-[1fr_40px_1fr] bg-muted/20 border-y border-border/50",
          isMobile && "cursor-pointer hover:bg-muted/30 transition-colors",
        )}
        onClick={() => isMobile && setStartersOpen(o => !o)}
      >
        <div className="px-2 py-1.5 text-right flex items-center justify-end gap-1">
          {isMobile && (
            <ChevronDown className={cn("w-3 h-3 text-muted-foreground transition-transform duration-200", startersOpen && "rotate-180")} />
          )}
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            {isMobile ? `Starters (${awayStarters.length})` : 'TP / Player / Stats'}
          </span>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            {isMobile ? '' : 'Slot'}
          </span>
        </div>
        <div className="px-2 py-1.5 flex items-center gap-1">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            {isMobile ? `Starters (${homeStarters.length})` : 'TP / Player / Stats'}
          </span>
          {isMobile && (
            <ChevronDown className={cn("w-3 h-3 text-muted-foreground transition-transform duration-200", startersOpen && "rotate-180")} />
          )}
        </div>
      </div>

      {/* Starter rows — each team shown independently, no cross-team slot matching */}
      {startersOpen && Array.from({ length: numStarterRows }).map((_, idx) => {
        const awayPlayer = awayStarters[idx];
        const homePlayer = homeStarters[idx];
        const awaySlot = awayPlayer ? displaySlotLabel(awayPlayer.slot || 'UTIL') : '';
        const homeSlot = homePlayer ? displaySlotLabel(homePlayer.slot || 'UTIL') : '';
        // Show shared label if both teams have the same slot, otherwise show both
        const centerLabel = awaySlot === homeSlot ? awaySlot : `${awaySlot}${awaySlot && homeSlot ? '/' : ''}${homeSlot}`;

        return (
          <div key={idx} className={cn("grid grid-cols-[1fr_40px_1fr]", idx % 2 === 0 ? "bg-background" : "bg-muted/10", "border-b border-border/30 last:border-b-0")}>
            <div className="border-r border-border/20">
              {renderPlayerCell(awayPlayer, 'away')}
            </div>
            <div className="flex items-center justify-center border-r border-border/20 bg-muted/20">
              <span className="text-[10px] font-bold text-muted-foreground">{centerLabel}</span>
            </div>
            <div>
              {renderPlayerCell(homePlayer, 'home')}
            </div>
          </div>
        );
      })}

      {/* Bench section — collapsible, shows points */}
      {(awayBench.length > 0 || homeBench.length > 0) && (
        <>
          <div
            className={cn(
              "grid grid-cols-[1fr_40px_1fr] bg-muted/20 border-t border-border/50 cursor-pointer hover:bg-muted/30 transition-colors",
            )}
            onClick={() => setBenchOpen(o => !o)}
          >
            <div className="px-2 py-1.5 text-right flex items-center justify-end gap-1">
              <ChevronDown className={cn("w-3 h-3 text-muted-foreground/60 transition-transform duration-200", benchOpen && "rotate-180")} />
              <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                Bench ({awayBench.length}){awayBenchPts !== 0 && <span className="ml-1 text-accent/70">{awayBenchPts > 0 ? '+' : ''}{awayBenchPts.toFixed(1)}</span>}
              </span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-[9px] font-bold text-muted-foreground/40">BN</span>
            </div>
            <div className="px-2 py-1.5 flex items-center gap-1">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                Bench ({homeBench.length}){homeBenchPts !== 0 && <span className="ml-1 text-accent/70">{homeBenchPts > 0 ? '+' : ''}{homeBenchPts.toFixed(1)}</span>}
              </span>
              <ChevronDown className={cn("w-3 h-3 text-muted-foreground/60 transition-transform duration-200", benchOpen && "rotate-180")} />
            </div>
          </div>
          {benchOpen && Array.from({ length: Math.max(awayBench.length, homeBench.length) }).map((_, i) => (
            <div key={`bench-${i}`} className={cn("grid grid-cols-[1fr_40px_1fr] opacity-70", i % 2 === 0 ? "bg-background" : "bg-muted/10", "border-b border-border/20 last:border-b-0")}>
              <div className="border-r border-border/20">
                {awayBench[i] ? renderPlayerCell(awayBench[i], 'away') : null}
              </div>
              <div className="flex items-center justify-center border-r border-border/20 bg-muted/20">
                <span className="text-[9px] font-bold text-muted-foreground/40">BN</span>
              </div>
              <div>
                {homeBench[i] ? renderPlayerCell(homeBench[i], 'home') : null}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Former Roster section — dropped players, collapsed by default */}
      {(awayDropped.length > 0 || homeDropped.length > 0) && (
        <>
          <div
            className="grid grid-cols-[1fr_40px_1fr] bg-muted/20 border-t border-border/50 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => setDroppedOpen(o => !o)}
          >
            <div className="px-2 py-1.5 text-right flex items-center justify-end gap-1">
              <ChevronDown className={cn("w-3 h-3 text-muted-foreground/40 transition-transform duration-200", droppedOpen && "rotate-180")} />
              <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/40">
                Former ({awayDropped.length}){awayDroppedPts !== 0 && <span className="ml-1 text-accent/50">{awayDroppedPts > 0 ? '+' : ''}{awayDroppedPts.toFixed(1)}</span>}
              </span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-[8px] font-bold text-destructive/40">DR</span>
            </div>
            <div className="px-2 py-1.5 flex items-center gap-1">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/40">
                Former ({homeDropped.length}){homeDroppedPts !== 0 && <span className="ml-1 text-accent/50">{homeDroppedPts > 0 ? '+' : ''}{homeDroppedPts.toFixed(1)}</span>}
              </span>
              <ChevronDown className={cn("w-3 h-3 text-muted-foreground/40 transition-transform duration-200", droppedOpen && "rotate-180")} />
            </div>
          </div>
          {droppedOpen && Array.from({ length: Math.max(awayDropped.length, homeDropped.length) }).map((_, i) => (
            <div key={`dropped-${i}`} className={cn("grid grid-cols-[1fr_40px_1fr] opacity-60", i % 2 === 0 ? "bg-background" : "bg-muted/10", "border-b border-border/20 last:border-b-0")}>
              <div className="border-r border-border/20">
                {awayDropped[i] ? renderPlayerCell(awayDropped[i], 'away') : null}
              </div>
              <div className="flex items-center justify-center border-r border-border/20 bg-muted/20">
                <span className="text-[8px] font-bold text-destructive/40">DR</span>
              </div>
              <div>
                {homeDropped[i] ? renderPlayerCell(homeDropped[i], 'home') : null}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Points breakdown — visible when bench/dropped contributed points */}
      {showBreakdown && (
        <div className="grid grid-cols-[1fr_40px_1fr] bg-muted/30 border-t border-border/50 px-2 py-1.5">
          <div className="text-right space-y-0.5">
            <div className="text-[9px] text-muted-foreground/60">
              <span className="text-muted-foreground/40">Starters</span> <span className="font-medium tabular-nums">{awayStarterPts.toFixed(1)}</span>
            </div>
            {awayBenchPts !== 0 && <div className="text-[9px] text-muted-foreground/60">
              <span className="text-muted-foreground/40">Bench</span> <span className="font-medium tabular-nums">{awayBenchPts.toFixed(1)}</span>
            </div>}
            {awayDroppedPts !== 0 && <div className="text-[9px] text-muted-foreground/60">
              <span className="text-muted-foreground/40">Former</span> <span className="font-medium tabular-nums">{awayDroppedPts.toFixed(1)}</span>
            </div>}
          </div>
          <div />
          <div className="space-y-0.5">
            <div className="text-[9px] text-muted-foreground/60">
              <span className="font-medium tabular-nums">{homeStarterPts.toFixed(1)}</span> <span className="text-muted-foreground/40">Starters</span>
            </div>
            {homeBenchPts !== 0 && <div className="text-[9px] text-muted-foreground/60">
              <span className="font-medium tabular-nums">{homeBenchPts.toFixed(1)}</span> <span className="text-muted-foreground/40">Bench</span>
            </div>}
            {homeDroppedPts !== 0 && <div className="text-[9px] text-muted-foreground/60">
              <span className="font-medium tabular-nums">{homeDroppedPts.toFixed(1)}</span> <span className="text-muted-foreground/40">Former</span>
            </div>}
          </div>
        </div>
      )}

      {modalPlayerId && (
        <MLBPlayerCardModal playerId={modalPlayerId} open={!!modalPlayerId} onOpenChange={(open) => { if (!open) setModalPlayerId(null); }} />
      )}
    </div>
  );
}

export default MatchupBoxScore;
